import { AppError, ErrorCodes } from '@ywwwtseng/ywjs';
import BigNumber from 'bignumber.js';
import crypto from 'crypto';
import * as schema from '@/db/schema';
import { AMOUNT_TYPES } from '@/constants';
import type {
  GiftClaimTypeAPIRule,
  GiftMetadataWithoutSettings,
  GiftMetadataSettings,
  GiftMetadata,
  SelectGift,
  InsertGift,
} from '@/types';
import { generateSignature } from './app';
import { TelegramBot } from '@ywwwtseng/api/telegram-bot';

export const api = {
  fetchRule: async ({
    api_key,
    api_url,
  }: {
    api_key: string;
    api_url: string;
  }) => {
    const response = await fetch(api_url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': api_key,
      },
    });

    const data = (await response.json()) as {
      data: GiftClaimTypeAPIRule;
    };

    if (!api.checkRule(data.data)) return null;

    return data.data;
  },
  activity: {
    settings: async ({
      api_key,
      api_url,
    }: {
      api_key: string;
      api_url: string;
    }) => {
      const response = await fetch(api_url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-API-KEY': api_key,
        },
      });
      const data = (await response.json()) as {
        data: GiftMetadataSettings;
      };
      const settings = data.data;

      if (settings.some((setting) => !api.checkRule(setting.rule))) {
        return null;
      }

      return settings;
    },
  },
  checkRule: (rule?: GiftClaimTypeAPIRule) => {
    if (!rule) return false;

    for (const [, condition] of Object.entries(rule)) {
      if (typeof condition !== 'object') return false;

      for (const [op, value] of Object.entries(condition)) {
        switch (op) {
          case 'gt':
          case 'gte':
          case 'lt':
          case 'lte':
            if (typeof value !== 'number') return false;
            break;
          case 'eq':
            // eq 可以是任何型別 → 不檢查
            break;
          default:
            // 不允許其他 key
            return false;
        }
      }
    }

    return true;
  },
  validate: async ({
    metadata,
    user_id,
  }: {
    metadata: GiftMetadata;
    user_id: string;
  }) => {
    const api_url = metadata.api_url;
    const api_key = metadata.api_key;
    const rule = metadata.rule;
    const api_secret = metadata.api_secret;

    if (!api_url || !api_key) {
      throw new AppError(
        ErrorCodes.INVALID_PARAMS,
        'message.gift_api_url_and_api_key_missing'
      );
    }

    if (!rule) {
      throw new AppError(
        ErrorCodes.INVALID_PARAMS,
        'message.gift_claim_rules_missing'
      );
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-API-KEY': api_key,
    };

    if (api_secret) {
      headers['X-API-TIMESTAMP'] = Date.now().toString();
      headers['X-API-NONCE'] = crypto.randomBytes(16).toString('hex');
      headers['X-API-SIGNATURE'] = generateSignature(
        api_secret,
        { user_id },
        headers['X-API-NONCE'],
        headers['X-API-TIMESTAMP']
      );
    }

    const response = await fetch(`${api_url}?user_id=${user_id}`, {
      method: 'GET',
      headers,
    });

    const data = (await response.json()) as { data: Record<string, unknown> };

    for (const [attribute, value] of Object.entries(data.data)) {
      if (!rule[attribute]) {
        continue;
      }
      const op = Object.keys(rule[attribute])[0];
      if (!op) return false;

      switch (op) {
        case 'gt':
          if ((value as number) <= (rule[attribute][op] as number))
            return false;
          continue;
        case 'gte':
          if ((value as number) >= (rule[attribute][op] as number))
            return false;
          continue;
        case 'lt':
          if ((value as number) < (rule[attribute][op] as number)) return false;
          continue;
        case 'lte':
          if ((value as number) <= (rule[attribute][op] as number))
            return false;
          continue;
        case 'eq':
          if (value !== rule[attribute][op]) return false;
          continue;
        default:
          return false;
      }
    }

    return true;
  },
};

export const metadata = {
  validate: (claimType: string, metadata: GiftMetadataWithoutSettings) => {
    if (claimType === 'api' && (!metadata.api_url || !metadata.api_key)) {
      return false;
    } else if (claimType === 'code' && !metadata.claim_code) {
      return false;
    } else if (claimType === 'chat' && !metadata.chat_id) {
      return false;
    }

    return true;
  },
};

export const fees = {
  calculate: ({
    fees,
    amount,
  }: {
    amount: string;
    fees: { platform_fee_percent: number; chat_fee_percent: number };
  }) => {
    if (new BigNumber(amount).isZero()) {
      return {
        platform_fee: '0',
        chat_fee: '0',
        final_payable: '0',
      };
    }

    let platform_fee = new BigNumber(amount)
      .multipliedBy(fees.platform_fee_percent)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toFixed();

    if (platform_fee === '0') {
      platform_fee = '1';
    }

    let chat_fee = new BigNumber(amount)
      .multipliedBy(fees.chat_fee_percent)
      .integerValue(BigNumber.ROUND_FLOOR)
      .toFixed();

    if (chat_fee === '0' && fees.chat_fee_percent > 0) {
      chat_fee = '1';
    }

    const final_payable = new BigNumber(amount)
      .plus(platform_fee)
      .plus(chat_fee)
      .toFixed();

    return {
      platform_fee,
      chat_fee,
      final_payable,
    };
  },
};

export const lucky = {
  gaussian(total: BigNumber.Value, n: number) {
    const totalBN = new BigNumber(total);

    // 1️⃣ 隨機生成高斯分佈（Box-Muller）
    const mean = totalBN.div(n);
    const stdDev = mean.div(2); // 控制隨機度

    const raw = Array.from({ length: n }, () => {
      let u = 0,
        v = 0;
      while (u === 0) u = Math.random();
      while (v === 0) v = Math.random();
      const num = Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
      // 取絕對值，避免負值
      return new BigNumber(Math.abs(num));
    });

    // 2️⃣ 正規化比例 (sum = 1)
    const sum = raw.reduce((a, b) => a.plus(b), new BigNumber(0));
    const ratios = raw.map((r) => r.div(sum));

    // 3️⃣ 轉換成整數分配（使用 BigNumber）
    let parts = ratios.map((r) =>
      totalBN.times(r).integerValue(BigNumber.ROUND_FLOOR)
    );

    // 4️⃣ 修正誤差（保證總和 = total）
    const currentSum = parts.reduce((a, b) => a.plus(b), new BigNumber(0));
    let diff = totalBN.minus(currentSum);

    if (!diff.isZero()) {
      // 將誤差補到第一個元素（或隨機一個）
      const idx = Math.floor(Math.random() * n);
      parts[idx] = parts[idx].plus(diff);
    }

    // 5️⃣ 輸出字串格式
    return parts.map((p) => p.toFixed(0));
  },
};

export const getGiftAmount = (gift: SelectGift | InsertGift | { metadata: GiftMetadata; amount_type: string; total: string; id?: string }): number => {
  const metadata = gift.metadata as GiftMetadata;
  const settings = metadata.settings;
  const amount_type = gift.amount_type;

  if (metadata.total_count) {
    return metadata.total_count;
  }

  if (!metadata.settings) {
    return 0
  }

  if (amount_type === 'random') {
    return metadata.settings.length;
  }

  const claim_amount = settings[0].ca;

  if (!claim_amount) {
    throw new AppError(
      ErrorCodes.INVALID_PARAMS,
      'message.gift_claim_amount_required',
      gift.id ? {
        gift_id: gift.id,
      } : {}
    );
  }

  return new BigNumber(gift.total).dividedBy(claim_amount).toNumber();
};

export const calculateGiftTotalAmount = ({
  amount_type,
  claim_amount,
  gift_amount,
  total_amount,
}: {
  amount_type: string;
  claim_amount: string;
  gift_amount?: string;
  total_amount?: string;
}) => {
  let amount = '0';

  if (amount_type === AMOUNT_TYPES.FIXED && claim_amount && gift_amount) {
    amount = new BigNumber(claim_amount as string)
      .multipliedBy(gift_amount as string)
      .toFixed();
  } else if (amount_type === AMOUNT_TYPES.RANDOM && total_amount) {
    amount = total_amount as string;
  }

  return amount;
};
